import React, { useState, useMemo } from 'react';
import { Search, Plus } from 'lucide-react';
import AccessibleFormField from './AccessibleFormField';
import SelectedItemsList from './SelectedItemsList';
import ItemEditModal from './ItemEditModal';

const ItemSelector = ({
  items,
  selectedItems = [],
  onAdd,
  onRemove,
  onUpdate,
  categoryLabel,
  id,
  error,
  required = false,
  helpText
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);
  const [isCustom, setIsCustom] = useState(false);

  // Filter items based on search query
  const filteredItems = useMemo(() => {
    if (!searchQuery.trim()) {
      return items;
    }
    const query = searchQuery.toLowerCase();
    return items.filter(item => 
      item.name.toLowerCase().includes(query)
    );
  }, [items, searchQuery]);

  // Get IDs of already selected items (for predefined items)
  const selectedItemIds = useMemo(() => {
    return selectedItems
      .filter(item => !item.isCustom)
      .map(item => {
        const found = items.find(i => i.name === item.name);
        return found?.id;
      })
      .filter(Boolean);
  }, [selectedItems, items]);

  const handleAddPredefinedItem = (item) => {
    // Check if item is already selected
    const alreadySelected = selectedItems.some(
      selected => !selected.isCustom && selected.name === item.name
    );

    if (alreadySelected) {
      return;
    }

    // Open modal to set quantity and tags
    setEditingItem({ name: item.name, id: item.id, quantity: 1, tags: [], isCustom: false });
    setEditingIndex(null);
    setIsCustom(false);
    setIsModalOpen(true);
  };

  const handleAddCustomItem = () => {
    setEditingItem(null);
    setEditingIndex(null);
    setIsCustom(true);
    setIsModalOpen(true);
  };

  const handleEditItem = (item, index) => {
    setEditingItem(item);
    setEditingIndex(index);
    setIsCustom(item.isCustom);
    setIsModalOpen(true);
  };

  const handleRemoveItem = (index) => {
    if (onRemove) {
      onRemove(index);
    }
  };

  const handleSaveItem = (itemData) => {
    if (editingIndex !== null) {
      // Update existing item
      if (onUpdate) {
        onUpdate(editingIndex, itemData);
      }
    } else {
      // Add new item
      if (onAdd) {
        onAdd(itemData);
      }
    }
  };

  return (
    <div>
      <AccessibleFormField
        label={categoryLabel}
        id={id}
        error={error}
        required={required}
        helpText={helpText}
      >
        <div className="space-y-4">
          {/* Search Field */}
          <div className="relative">
            <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="input-field pr-10"
              placeholder="חפש פריט..."
              aria-label="חיפוש פריטים"
            />
          </div>

          {/* Items List */}
          <div className="border border-gray-200 rounded-lg p-4 bg-white max-h-64 overflow-y-auto">
            {filteredItems.length === 0 ? (
              <p className="text-center text-gray-500 py-4">
                {searchQuery ? 'לא נמצאו פריטים' : 'אין פריטים זמינים'}
              </p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {filteredItems.map((item) => {
                  const isSelected = selectedItemIds.includes(item.id);
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => handleAddPredefinedItem(item)}
                      disabled={isSelected}
                      className={`
                        text-right p-3 rounded-lg border-2 transition-all
                        ${isSelected
                          ? 'bg-gray-100 border-gray-300 text-gray-400 cursor-not-allowed'
                          : 'bg-white border-orange-200 hover:border-orange-400 hover:bg-orange-50 text-brown-700'
                        }
                      `}
                      aria-label={`הוסף ${item.name}`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{item.name}</span>
                        {!isSelected && (
                          <Plus className="h-4 w-4 text-orange-600 mr-2" />
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Add Custom Item Button */}
          <button
            type="button"
            onClick={handleAddCustomItem}
            className="w-full btn-secondary flex items-center justify-center gap-2"
          >
            <Plus className="h-5 w-5" />
            הוסף פריט מותאם
          </button>

          {/* Selected Items List */}
          <div className="mt-6">
            <h3 className="text-lg font-semibold text-brown-800 mb-4">
              פריטים שנבחרו ({selectedItems.length})
            </h3>
            <SelectedItemsList
              items={selectedItems}
              onEdit={handleEditItem}
              onRemove={handleRemoveItem}
            />
          </div>
        </div>
      </AccessibleFormField>

      {/* Edit Modal */}
      <ItemEditModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingItem(null);
          setEditingIndex(null);
        }}
        item={editingItem}
        onSave={handleSaveItem}
        isCustom={isCustom}
      />
    </div>
  );
};

export default ItemSelector;

